import {
  Camera,
  AllowedList,
  Captura,
  CapturingWindowWeek,
  CameraSummaryProps
} from './camera.model'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { environment } from '../../../environments/environment'

@Injectable({
  providedIn: 'root'
})
export class PrivateCameraService {
  constructor(private http: HttpClient) {}

  setCameraForm(
    cameraID: string,
    estabelecimento: string,
    name: string,
    address: string,
    port: string,
    camera_user: string,
    camera_password: string,
    model: string,
    resolution: string,
    tipo: string,
    fps: string,
    hour_init: string,
    hour_final: string,
    allowed_list: AllowedList[],
    bucket_url: string,
    capturasArray: Captura[],
    timezone: string,
    capturingWindows: CapturingWindowWeek
  ): Camera {
    const cameraObject = {
      _id: cameraID,
      estabelecimento,
      name,
      address,
      port,
      camera_user,
      camera_password,
      model,
      resolution,
      tipo,
      fps,
      hour_init,
      hour_final,
      allowed_list,
      bucket_url,
      capturasArray,
      timezone,
      capturing_windows: capturingWindows
    }
    return cameraObject
  }

  /**
   * Retorna todas as informacoes da Camera buscada.
   * @param {string} estabelecimentoID - ID do estabelecimento.
   * @param {string} cameraID - ID da camera.
   */
  getCamera(estabelecimentoID: string, cameraID: string): Observable<[Camera]> {
    return this.http.get<any>(`${environment.apiURL}/camera/${estabelecimentoID}/${cameraID}`, {
      withCredentials: true
    })
  }

  /**
   * Retorna todas as Cameras cadastradas dentro do estabelecimento buscado.
   * @param {string} estabelecimentoID - ID do estabelecimento.
   */
  getCamerasFromEstabelecimento(
    estabelecimentoID: string,
    query?: 'checkout' | 'pista'
  ): Observable<CameraSummaryProps[]> {
    const param = query ? `tipo=${query}` : ''
    return this.http.get<any>(`${environment.apiURL}/camera/${estabelecimentoID}?${param}`, {
      withCredentials: true
    })
  }

  /**
   * Atualiza os dados da Camera passada atraves dos parametros fornecidos.
   * @param {string} cameraID - ID da Camera que será atualizada.
   * @param {string} estabelecimento - ID do estabelecimento ao qual essa camera pertence.
   * @param {string} name - String contendo o nome da Camera.
   * @param {string} address - String com o endereco IP da Camera.
   * @param {string} port - String contendo a Porta deste endereço IP.
   * @param {string} camera_user - String com o valor do usuario da Camera.
   * @param {string} camera_password - String com a senha referente ao usuario da Camera.
   * @param {string} model - String com o tipo de modelo da Camera.
   * @param {string} resolution - String com o valor de resolucao selecionado.
   * @param {string} tipo - String que representa o Tipo da Camera selecionado.
   * @param {string} fps - String com o valor de FPS escolhido.
   * @param {string} hour_init - String com o horario de inicio das gravacoes desta Camera.
   * @param {string} hour_final - String com o horario de termino das gravacoes desta Camera.
   * @param {AllowedList[]} allowed_list - Array do tipo AllowedList, contendo os usuarios que terao acesso a esta Camera.
   * @param {string} bucket_url - Array de strings representando as url dos buckets.
   * @param {Captura[]} capturasArray - Array de Capturas, contendo os horarios onde as capturas serao feitas para esta Camera.
   */
  cadastrarCamera(
    cameraID: string,
    estabelecimento: string,
    name: string,
    address: string,
    port: string,
    camera_user: string,
    camera_password: string,
    model: string,
    resolution: string,
    tipo: string,
    fps: string,
    hour_init: string,
    hour_final: string,
    allowed_list: AllowedList[],
    bucket_url: string,
    capturasArray: Captura[],
    timezone: string,
    capturingWindows: CapturingWindowWeek
  ): Observable<any> {
    const payload = this.setCameraForm(
      cameraID,
      estabelecimento,
      name,
      address,
      port,
      camera_user,
      camera_password,
      model,
      resolution,
      tipo,
      fps,
      hour_init,
      hour_final,
      allowed_list,
      bucket_url,
      capturasArray,
      timezone,
      capturingWindows
    )
    return this.http.post<any>(`${environment.apiURL}/camera`, payload, {
      withCredentials: true,
      responseType: 'text' as 'json'
    })
  }

  deleteCamera(estabelecimentoID: string, cameraID: string): Observable<any> {
    return this.http.delete<any>(`${environment.apiURL}/camera/${estabelecimentoID}/${cameraID}`, {
      withCredentials: true,
      responseType: 'text' as 'json'
    })
  }

  setCameraObject(cameraObject: Camera): Observable<any> {
    return this.http.put<any>(`${environment.apiURL}/camera`, cameraObject, {
      withCredentials: true,
      responseType: 'text' as 'json'
    })
  }

  /**
   * Atualiza os dados da Camera passada atraves dos parametros fornecidos.
   * @param {string} cameraID - ID da Camera que será atualizada.
   * @param {string} estabelecimento - ID do estabelecimento ao qual essa camera pertence.
   * @param {string} name - String contendo o nome da Camera.
   * @param {string} address - String com o endereco IP da Camera.
   * @param {string} port - String contendo a Porta deste endereço IP.
   * @param {string} camera_user - String com o valor do usuario da Camera.
   * @param {string} camera_password - String com a senha referente ao usuario da Camera.
   * @param {string} model - String com o tipo de modelo da Camera.
   * @param {string} resolution - String com o valor de resolucao selecionado.
   * @param {string} tipo - String que representa o Tipo da Camera selecionado.
   * @param {string} fps - String com o valor de FPS escolhido.
   * @param {string} hour_init - String com o horario de inicio das gravacoes desta Camera.
   * @param {string} hour_final - String com o horario de termino das gravacoes desta Camera.
   * @param {AllowedList[]} allowed_list - Array do tipo AllowedList, contendo os usuarios que terao acesso a esta Camera.
   * @param {string} bucket_url - Array de strings representando as url dos buckets.
   * @param {Captura[]} capturasArray - Array de Capturas, contendo os horarios onde as capturas serao feitas para esta Camera.
   */
  updateCamera(
    cameraID: string,
    estabelecimento: string,
    name: string,
    address: string,
    port: string,
    camera_user: string,
    camera_password: string,
    model: string,
    resolution: string,
    tipo: string,
    fps: string,
    hour_init: string,
    hour_final: string,
    allowed_list: AllowedList[],
    bucket_url: string,
    capturasArray: Captura[],
    timezone: string,
    capturingWindows: CapturingWindowWeek
  ): Observable<any> {
    const payload = this.setCameraForm(
      cameraID,
      estabelecimento,
      name,
      address,
      port,
      camera_user,
      camera_password,
      model,
      resolution,
      tipo,
      fps,
      hour_init,
      hour_final,
      allowed_list,
      bucket_url,
      capturasArray,
      timezone,
      capturingWindows
    )
    return this.http.put<any>(`${environment.apiURL}/camera`, payload, {
      withCredentials: true,
      responseType: 'text' as 'json'
    })
  }

  addUserToCameraAllowedList(
    cameraId: string,
    shopId: string,
    allowedList: AllowedList[]
  ): Observable<any> {
    const payload = {
      _id: cameraId,
      estabelecimento: shopId,
      allowed_list: allowedList
    }
    return this.http.put<any>(`${environment.apiURL}/camera/addusersinallowedlist`, payload, {
      withCredentials: true,
      responseType: 'text' as 'json'
    })
  }

  partiallyUpdateCamera(payload: Partial<Camera>) {
    return this.http.put<any>(`${environment.apiURL}/camera`, payload, {
      withCredentials: true,
      responseType: 'text' as 'json'
    })
  }
}
